import { useTranslation } from 'react-i18next';
import { ClientOnly } from 'remix-utils/client-only';
import { ImageBleed, PageType } from 'types';
import { ProductListItems } from 'types/capi/article';
import type {
  Affiliate,
  Image as ImageType,
  ProductListItemExternal,
  ProductListItemInlineExternal,
  ProductListItemInternal
} from 'types/capi/shared';
import { fromProductListItem } from 'types/TrackableProduct';

import ExternalIcon from '~/assets/icons/ExternalIcon';
import AffiliatePageLink from '~/components/AffiliatePageLink';
import AffiliateProductLink from '~/components/AffiliateProductLink';
import Countdown from '~/components/Countdown';
import Image from '~/components/Image';
import Price from '~/components/Price';
import ProductCard from '~/components/ProductCard';
import ProductNotificationButton from '~/components/ProductNotificationButton';
import Tracked from '~/components/Tracked';
import type { StockStatus as StockStatusType } from '~/domain/stockStatus';
import { getStockStatus, textColours } from '~/domain/stockStatus';
import useIsPendingConfirmation from '~/hooks/useIsPendingConfirmation';
import useUser from '~/hooks/useUser';
import { formatReleaseDateLong } from '~/utils/dates';

import { getBleedClass } from './ModuleRow';

const MODULE_TYPE = 'product-list';
const BLOCK_POSITION = 1;

type CatalogProduct = ProductListItemExternal | ProductListItemInternal;

export default function ProductListBlock({
  backgroundImage,
  backgroundColor,
  bleed,
  columnCount,
  modulePosition,
  pageType,
  product
}: {
  backgroundColor?: string;
  backgroundImage?: ImageType;
  bleed?: ImageBleed;
  columnCount?: number;
  modulePosition?: number;
  pageType: PageType;
  product: ProductListItems[number];
}) {
  const isInlineExternal = product.type === 'inline-external';
  const isBleedColumn = bleed && bleed !== 'none';

  const title = !isInlineExternal && (
    <span
      className={`text-title-xl text-white ${columnCount! > 2 && 'md:text-[32px]'}`}
    >
      {product.name}
    </span>
  );

  const productInfo = isInlineExternal ? (
    <div className="flex flex-col gap-6 rounded-lg bg-white p-6">
      <div className="flex flex-col gap-2">
        <h3 className="text-title-lg">{product.name}</h3>
        <span className="text-base tracking-[-0.16px] text-grey-read">
          <Price
            regular={product.price.regular}
            sale={product.price.sale}
            currency={product.price.currency}
          />
        </span>
      </div>

      {product.affiliate && (
        <AffiliatePageLink
          affiliate={product.affiliate}
          urlTemplates={product}
          buttonName="PRODUCT_CARD"
          pagePosition="Featured Product Block"
          variant="forest"
          className="flex h-12 items-center justify-center gap-2"
        >
          <ExternalIcon color="#FFFFFF" size={16} />
          Buy now from {product.affiliate.name}
        </AffiliatePageLink>
      )}
    </div>
  ) : (
    <div className="rounded-lg bg-white px-3 py-4">
      {['in-stock', 'reseller'].includes(product.stock_status) ? (
        <InStockProductInfo product={product} />
      ) : (
        <OutOfStockProductInfo product={product} />
      )}
    </div>
  );

  const productImage = (
    <ProductImage
      bleed={bleed}
      columnCount={columnCount}
      isInlineExternal={isInlineExternal}
      modulePosition={modulePosition}
      pageType={pageType}
      product={product}
    />
  );

  return (
    <Backdrop
      bleed={bleed}
      columnCount={columnCount}
      image={backgroundImage}
      backgroundColor={backgroundColor}
      productImage={product.image}
    >
      {columnCount === 2 ? (
        <>
          {title}
          <div className="flex w-full flex-col items-center justify-between gap-6 lg:h-[216px] lg:flex-row">
            <div className="flex w-full flex-col gap-5">{productInfo}</div>
            {productImage}
          </div>
        </>
      ) : columnCount === 3 && isBleedColumn ? (
        <>
          <div className="hidden w-full flex-col items-center gap-6 xxl:flex">
            {title}
            <div className="flex w-full flex-col items-center justify-between gap-6 lg:h-[216px] lg:flex-row">
              <div className="flex w-full flex-col gap-5">{productInfo}</div>
              {productImage}
            </div>
          </div>
          <div className="flex w-full flex-col items-center gap-6 xxl:hidden">
            <div className="flex w-full flex-col gap-5 md:max-w-[289px]">
              {title}
              {productInfo}
            </div>
            <div className="lg:w-[264px]">{productImage}</div>
          </div>
        </>
      ) : columnCount === 3 ? (
        <>
          <div className="flex w-full flex-col gap-5 md:max-w-[289px]">
            {title}
            {productInfo}
          </div>
          <div className="lg:w-[264px]">{productImage}</div>
        </>
      ) : columnCount === 4 ? (
        <>
          <div className="flex w-full flex-col gap-5 md:order-2 md:max-w-[232px]">
            {title}
            {productInfo}
          </div>
          <div className="md:order-1 md:w-[216px]">{productImage}</div>
        </>
      ) : (
        <>
          <div className="flex w-full flex-col gap-5 md:max-w-[552px]">
            {title}
            {productInfo}
          </div>
          <div className="md:w-[360px]">{productImage}</div>
        </>
      )}
    </Backdrop>
  );
}

function AffiliateLogo({ affiliate }: { affiliate: Affiliate }) {
  return (
    <Image
      className="w-14"
      src={affiliate.logo}
      loading="lazy"
      alt={`${affiliate.name}-logo`}
      responsiveOptions={{
        xs: { width: 150 }
      }}
    />
  );
}

function Backdrop({
  bleed,
  children,
  columnCount,
  image,
  backgroundColor,
  productImage
}: {
  backgroundColor?: string;
  bleed?: ImageBleed;
  children: React.ReactNode;
  columnCount?: number;
  image?: ImageType;
  productImage?: ImageType;
}) {
  const hasBleed = bleed && bleed !== 'none';
  const is3ColBleed = columnCount === 3 && hasBleed;
  const bleedClass = getBleedClass(bleed ?? 'none', columnCount ?? 1);

  const panelBleedClass = is3ColBleed
    ? `desktop:[--bleed:max(0px,calc(50cqw-35.25rem))] desktop:w-[calc(100%+var(--bleed)-94px)] ${
        bleed === 'left'
          ? 'desktop:ml-[calc(var(--bleed)*-1)]'
          : 'desktop:ml-[94px]'
      }`
    : bleedClass;

  return (
    <div
      className={`relative z-10 flex justify-center overflow-hidden ${
        columnCount === 4 ? 'py-6' : 'py-10'
      } ${panelBleedClass} ${columnCount! > 1 ? '-mx-4 md:mx-0 lg:!px-0' : ''}`}
    >
      {image ? (
        <Image
          loading="eager"
          className="absolute inset-0 -z-40 block size-full object-cover"
          alt={image.alt}
          src={image.url}
        />
      ) : productImage ? (
        <img
          loading="eager"
          className="absolute inset-0 -z-40 block size-full scale-[160%] object-cover blur-lg"
          alt=""
          src={productImage.url}
        />
      ) : (
        <div
          className="absolute inset-0 -z-40 block size-full"
          style={{ backgroundColor: backgroundColor ?? '#000000' }}
        />
      )}
      <div className="absolute inset-0 -z-30 bg-black opacity-40"></div>
      <div
        className={`flex flex-col items-center justify-center gap-6 xs:w-full xs:max-w-[384px] ${
          !columnCount || columnCount === 1
            ? 'content-width md:max-w-desktop md:flex-row md:justify-between desktop:px-0'
            : columnCount === 2
              ? 'px-4 xs:w-full md:px-6 lg:w-full lg:max-w-[536px] lg:px-4'
              : columnCount === 3
                ? `px-4 md:flex-col md:px-4 lg:px-6 ${
                    is3ColBleed ? 'xxl:max-w-[536px]' : ''
                  }`
                : columnCount === 4
                  ? 'px-4 md:flex-col md:px-4'
                  : ''
        }`}
      >
        {children}
      </div>
    </div>
  );
}

function InStockProductInfo({ product }: { product: CatalogProduct }) {
  return (
    <div className="flex w-full flex-col gap-4 rounded-md bg-white">
      <div className="mb-2 flex w-full flex-row items-center justify-between">
        <div className="flex items-center gap-1">
          <AffiliateLogo affiliate={product.affiliate} />
          <StockStatus status={product.stock_status} />
        </div>
        <Price {...product.price} className="flex gap-1" />
      </div>
      <InStockSizes product={product} />
      {product.type === 'external' ? (
        <AffiliateProductLink
          className="flex h-12 items-center justify-center"
          affiliate={product.affiliate}
          pagePosition="Featured Product Block"
          buttonPosition={-1}
          price={product.price}
          product={fromProductListItem(product)}
          urlTemplates={{
            click_ref_template: product.click_ref_template,
            url: product.url,
            url_template: product.url_template
          }}
          variant="forest"
        >
          Buy now from {product.affiliate.name}
        </AffiliateProductLink>
      ) : (
        <Tracked.InternalLink
          to={product.path}
          variant="forest"
          name="PRODUCT_CARD"
          pagePosition="Featured Product Block"
        >
          Buy now from {product.affiliate.name}
        </Tracked.InternalLink>
      )}
    </div>
  );
}

function InStockSizes({ product }: { product: CatalogProduct }) {
  if (!product.sizes?.length) return null;

  return (
    <ul className="flex	flex-wrap items-center gap-1">
      <li className="mr-2 text-xs tracking-wide text-grey-hide">
        UK sizes in stock:
      </li>
      {product.sizes.map((size) =>
        product.type === 'external' ? (
          <AffiliateProductLink
            key={`sl-size-${size}`}
            affiliate={product.affiliate}
            price={product.price}
            product={fromProductListItem(product)}
            buttonPosition={-1}
            pagePosition="Featured Product Block"
            urlTemplates={{
              click_ref_template: product.click_ref_template,
              url: product.url,
              url_template: product.url_template
            }}
            variant="none"
          >
            <Size>{size}</Size>
          </AffiliateProductLink>
        ) : (
          <Tracked.InternalLink
            key={`sl-size-${size}`}
            to={product.path}
            variant="none"
            name="PRODUCT_CARD"
            pagePosition="Featured Product Block"
          >
            <Size>{size}</Size>
          </Tracked.InternalLink>
        )
      )}
    </ul>
  );
}

function NotificationTooltip({
  isNotified,
  isPendingConfirmation,
  stockStatus
}: {
  isNotified: boolean;
  isPendingConfirmation?: boolean;
  stockStatus?: StockStatusType;
}) {
  if (isNotified) return null;

  return (
    <div className="translate-y-[-120%] animate-slide-from-top text-clip">
      {isPendingConfirmation ? (
        <div className="bg-orange-100 p-2 text-center text-xs text-black">
          Set up incomplete
        </div>
      ) : (
        <div className="bg-purple-hola-lm p-2 text-center text-xs text-purple-hola">
          {stockStatus === 'sold-out'
            ? 'Get a restock alert'
            : 'Get a release alert'}
        </div>
      )}
    </div>
  );
}

function OutOfStockProductInfo({ product }: { product: CatalogProduct }) {
  const isNotified = useUser().isNotificationSetForProduct(product.legacy_id);
  const stockStatus = getStockStatus(product.stock_status);
  const isPendingConfirmation = useIsPendingConfirmation(
    product.id,
    isNotified
  );

  const isUpcoming =
    stockStatus === 'coming-soon' || stockStatus === 'upcoming';

  return (
    <div className="flex w-full flex-col gap-4 rounded-md bg-white">
      <div className="flex items-center justify-between">
        <div className="flex flex-col items-start">
          <StockStatus status={product.stock_status} />
          {isUpcoming && product.date.released && (
            <div className="text-sm font-light text-grey-read">
              {formatReleaseDateLong(product.date.released)}
            </div>
          )}
        </div>
        {isUpcoming && product.date.released && (
          <ClientOnly>
            {() => <Countdown toUnixTimestamp={product.date.released!} />}
          </ClientOnly>
        )}
      </div>
      <div className="flex w-full flex-col gap-2">
        <ProductNotificationButton
          product={{
            affiliateName:
              product.type === 'external' ? product.affiliate.name : undefined,
            id: product.id,
            image: product.image,
            legacyId: product.legacy_id,
            name: product.name,
            price: product.price.regular
          }}
          className="h-12 w-full"
          pagePosition="Dynamic Product Block"
          buttonStyle="normal"
        />
        <NotificationTooltip
          stockStatus={getStockStatus(product.stock_status)}
          isNotified={isNotified}
          isPendingConfirmation={isPendingConfirmation}
        />
      </div>
    </div>
  );
}

function ProductImage({
  bleed,
  columnCount,
  isInlineExternal,
  modulePosition,
  pageType,
  product
}: {
  bleed?: ImageBleed;
  columnCount?: number;
  isInlineExternal: boolean;
  modulePosition?: number;
  pageType: PageType;
  product: CatalogProduct | ProductListItemInlineExternal;
}) {
  const isBleed = bleed && bleed !== 'none';

  return (
    <div
      className={`w-full max-w-[552px] ${
        !columnCount || columnCount === 1
          ? 'md:w-[360px]'
          : columnCount === 2
            ? 'lg:max-w-[216px]'
            : columnCount === 3
              ? `md:max-w-[360px] ${isBleed ? 'xxl:max-w-[216px]' : ''}`
              : columnCount === 4
                ? 'lg:max-w-[216px]'
                : ''
      }`}
    >
      {isInlineExternal ? (
        <ProductCard.InlineExternal
          cardSize="large"
          padImage
          pagePosition="Featured Product Block"
          product={product as ProductListItemInlineExternal}
          withMetadata={false}
        />
      ) : product.type === 'external' ? (
        <ProductCard.External
          allowNotifications
          cardSize="large"
          modulePosition={modulePosition}
          moduleType={MODULE_TYPE}
          padImage
          openPdp={false}
          position={BLOCK_POSITION}
          pagePosition="Featured Product Block"
          product={product}
          pageType={pageType}
          withMetadata={false}
        />
      ) : (
        <ProductCard.Internal
          allowNotifications
          cardSize="large"
          modulePosition={modulePosition}
          moduleType={MODULE_TYPE}
          name="PRODUCT_CARD"
          padImage
          position={BLOCK_POSITION}
          pageType={pageType}
          pagePosition="Featured Product Block"
          product={product as ProductListItemInternal}
          withMetadata={false}
        />
      )}
    </div>
  );
}

function Size({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex h-5 items-center rounded-xl  border border-grey-divide bg-white p-2 text-xs text-grey-hide">
      {children}
    </li>
  );
}

function StockStatus({ status }: { status: string }) {
  const { t } = useTranslation();
  const stockStatus = getStockStatus(status);

  if (!stockStatus) return null;

  return (
    <div className={`text-base tracking-wide ${textColours[stockStatus]}`}>
      {t(stockStatus)}
    </div>
  );
}
